import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { MoreHorizontal } from 'lucide-react';
import { useAuth } from '@edusense/shared-auth-core';
import { Button, Avatar } from '@edusense/shared-ui-kit';
import { cn } from '@edusense/shared-ui-kit/utils';
import HeaderActionMenu from '../HeaderActionMenu/HeaderActionMenu';
import { AuthorDisplay } from '@edusense/shared-ui';
import RetweetIndicator from './RetweetIndicator';
import ThreadLine from './ThreadLine';
import { StyledHeaderContainer, ActionButtonWrapper, ReplyToText, HeaderWrapper } from './SocialHeader.styles';
import { useResponsiveSize, sizeToPixels } from './useResponsiveSize';
/**
 * 통합된 헤더 컴포넌트로 사용자 및 트윗 헤더를 모두 지원합니다.
 */ const SocialHeader = ({ author, createdAt, showTimestamp = true, size = 'md', showActions = true, variant = 'default', isRetweet = false, retweetAuthor, tweetId, showThreadLineTop = false, showThreadLineBottom = false, replyToUser, customRenderAvatar, customRenderActionMenu, actions, onAuthorClick, className, children })=>{
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const avatarRef = useRef(null);
    const headerRef = useRef(null);
    const actionMenuTriggerRef = useRef(null);
    // 안전한 navigate 호출을 위한 래퍼 함수
    let navigate;
    try {
        navigate = useNavigate();
    } catch (error) {
        navigate = (path)=>{
            console.log(`라우팅 컨텍스트 없음, 이동할 수 없습니다: ${path}`);
        };
    }
    // 반응형 사이즈 계산
    const effectiveAuthorDisplaySize = useResponsiveSize(size);
    const avatarSizeInPx = sizeToPixels(effectiveAuthorDisplaySize);
    const { isAuthenticated } = useAuth();
    // 메뉴 토글 핸들러
    const toggleMenu = (e)=>{
        e.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
    };
    return /*#__PURE__*/ React.createElement(HeaderWrapper, {
        className: cn('w-full relative', className),
        ref: headerRef
    }, isRetweet && retweetAuthor && /*#__PURE__*/ React.createElement(RetweetIndicator, {
        retweetAuthor: retweetAuthor
    }), /*#__PURE__*/ React.createElement(StyledHeaderContainer, {
        size: size
    }, /*#__PURE__*/ React.createElement("div", {
        className: "relative",
        style: {
            minHeight: `${avatarSizeInPx}px`
        },
        ref: avatarRef,
        "data-avatar-container": true
    }, /*#__PURE__*/ React.createElement(ThreadLine, {
        showTop: showThreadLineTop,
        showBottom: showThreadLineBottom,
        avatarSize: avatarSizeInPx,
        avatarRef: avatarRef,
        headerRef: headerRef
    }), /*#__PURE__*/ React.createElement("div", {
        className: "relative z-[20]"
    }, customRenderAvatar ? customRenderAvatar(author, effectiveAuthorDisplaySize) : /*#__PURE__*/ React.createElement("div", {
        className: "cursor-pointer",
        onClick: ()=>{
            if (onAuthorClick) {
                onAuthorClick(author);
            } else {
                navigate(`/user/${author.username || author.id}`);
            }
        }
    }, /*#__PURE__*/ React.createElement(Avatar, {
        username: author.username,
        name: author.name,
        profileImage: author.profileImageUrl,
        size: effectiveAuthorDisplaySize,
        alt: `${author.name}의 프로필`
    })))), /*#__PURE__*/ React.createElement("div", {
        className: "flex-1 min-w-0 mb-2"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex items-center justify-between"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex-1 pl-0"
    }, /*#__PURE__*/ React.createElement("div", {
        onClick: (e)=>{
            e.stopPropagation();
            e.preventDefault();
            if (onAuthorClick) {
                onAuthorClick(author);
            } else {
                navigate(`/user/${author.username}`);
            }
        },
        className: "cursor-pointer hover:opacity-80 relative z-30"
    }, /*#__PURE__*/ React.createElement(AuthorDisplay, {
        author: {
            id: author.id,
            name: author.name,
            username: author.username,
            profileImage: author.profileImageUrl,
            verified: author.verified
        },
        createdAt: createdAt,
        showTimestamp: showTimestamp,
        size: effectiveAuthorDisplaySize,
        hideAvatar: true,
        layout: "inline"
    })), replyToUser && /*#__PURE__*/ React.createElement(ReplyToText, null, /*#__PURE__*/ React.createElement("span", null, "@", replyToUser.username, "님에게 답글"))), showActions && isAuthenticated && /*#__PURE__*/ React.createElement(ActionButtonWrapper, null, /*#__PURE__*/ React.createElement(Button, {
        ref: actionMenuTriggerRef,
        iconOnly: true,
        icon: /*#__PURE__*/ React.createElement(MoreHorizontal, {
            className: "h-4 w-4"
        }),
        onClick: toggleMenu,
        className: "text-gray-500 hover:text-primary hover:bg-primary/10 z-10",
        label: "더 보기",
        size: "xs",
        animation: "none",
        variant: "ghost"
    }), /*#__PURE__*/ React.createElement(HeaderActionMenu, {
        isOpen: isMenuOpen,
        onClose: ()=>setIsMenuOpen(false),
        triggerRef: actionMenuTriggerRef,
        actions: actions,
        customRenderActionMenu: customRenderActionMenu
    }))), children)));
};
export default SocialHeader;
